'use client'

import {useLayoutEffect, useRef, useState} from 'react'
import {cn} from '../../../lib/utils'

type PublicPartnerLogoSize = 'compact' | 'standard' | 'large' | 'wide' | 'tall'

type PublicPartnerLogoProps = {
    className?: string
    onVisibilityChange?: (isVisible: boolean) => void
    src: string
    testId?: string
}

type PublicPartnerLogoState = {
    hasImageError: boolean
    size: PublicPartnerLogoSize
    src: string
}

export function PublicPartnerLogo({
    className,
    onVisibilityChange,
    src,
    testId = 'public-partner-logo',
}: PublicPartnerLogoProps) {
    const imageRef = useRef<HTMLImageElement | null>(null)
    const [state, setState] = useState<PublicPartnerLogoState>({
        hasImageError: false,
        size: 'standard',
        src,
    })
    const isCurrentSrc = state.src === src
    const hasImageError = isCurrentSrc ? state.hasImageError : false
    const size = isCurrentSrc ? state.size : 'standard'

    useLayoutEffect(() => {
        const image = imageRef.current
        if (!image?.complete) return
        if (image.naturalWidth === 0) {
            onVisibilityChange?.(false)
            setState({
                hasImageError: true,
                size: 'standard',
                src,
            })
            return
        }
        onVisibilityChange?.(true)
        setState({
            hasImageError: false,
            size: resolveLogoSize(image.naturalWidth, image.naturalHeight),
            src,
        })
    }, [onVisibilityChange, src])

    if (hasImageError) return null

    return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
            ref={imageRef}
            src={src}
            alt=""
            className={cn(
                'shrink-0 rounded-[18px] border border-slate-100 dark:border-gray-700 shadow-[0_10px_22px_rgba(57,98,198,0.07)]',
                getLogoSizeClassName(size),
                className,
            )}
            data-logo-size={size}
            data-testid={testId}
            loading="lazy"
            onError={() => {
                onVisibilityChange?.(false)
                setState({
                    hasImageError: true,
                    size: 'standard',
                    src,
                })
            }}
            onLoad={(event) => {
                onVisibilityChange?.(true)
                setState({
                    hasImageError: false,
                    size: resolveLogoSize(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight),
                    src,
                })
            }}
        />
    )
}

function resolveLogoSize(width: number, height: number): PublicPartnerLogoSize {
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return 'standard'
    }

    const ratio = width / height
    const longestSide = Math.max(width, height)

    if (ratio >= 1.8) {
        return 'wide'
    }

    if (ratio <= 0.72) {
        return 'tall'
    }

    if (longestSide >= 900) {
        return 'large'
    }

    if (longestSide < 320) {
        return 'compact'
    }

    return 'standard'
}

function getLogoSizeClassName(size: PublicPartnerLogoSize): string {
    switch (size) {
        case 'compact':
            return 'h-24 w-24 object-cover md:h-28 md:w-28'
        case 'large':
            return 'h-36 w-36 object-cover md:h-40 md:w-40'
        case 'wide':
            return 'h-auto max-h-28 w-auto max-w-[min(56vw,280px)] object-contain md:max-h-32 md:max-w-[320px]'
        case 'tall':
            return 'h-36 w-auto max-w-28 object-contain md:h-40 md:max-w-32'
        case 'standard':
        default:
            return 'h-32 w-32 object-cover md:h-36 md:w-36'
    }
}
